import './AllFiles.css';
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router";
import { useSearchParams } from "react-router-dom";
import { FaFilePdf, FaPlus, FaTrash } from "react-icons/fa";
import { fetchFilesBySubject, setCurrentSubject, setCurrentPage } from "../../redux/slices/filesSlice";
import { getStudentSubjects } from "../../redux/slices/subjectsSlice";
import { getTeacherClasses } from "../../redux/slices/lessonsSlice";
import { fetchTeacherClassFiles, deleteTeacherFile, setCurrentPage as setTeacherFilesPage } from "../../redux/slices/teacherFilesSlice";
import AddTeacherFileModal from "../../components/AddTeacherFileModal/AddTeacherFileModal";
import { toast } from "react-toastify";

function AllFiles() {
    const { isDarkMode } = useDarkMode();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { files, loading, error, pagination, currentSubject, currentPage } = useSelector((state) => state.files);
    const { studentSubjects, subjectsLoading } = useSelector((state) => state.subjects);
    const { currentUser, profileData } = useSelector((state) => state.auth);
    const { teacherClasses } = useSelector((state) => state.lessons);
    const { files: teacherFiles, loading: teacherFilesLoading, error: teacherFilesError, pagination: teacherFilesPagination, currentPage: teacherFilesCurrentPage } = useSelector((state) => state.teacherFiles);
    const [activeTab, setActiveTab] = useState("الكل");
    const [isAddFileModalOpen, setIsAddFileModalOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const isAuthenticated = !!localStorage.getItem("token3almny");
    const isTeacher = currentUser?.role === 'teacher';
    const [selectedClassId, setSelectedClassId] = useState(null);

    useEffect(() => {
        if (isAuthenticated) {
            if (isTeacher) {
                // For teachers, fetch their classes first
                dispatch(getTeacherClasses());
            } else {
                // For students, fetch subjects and files
                dispatch(getStudentSubjects());
                dispatch(fetchFilesBySubject({ subjectId: 0 }));
            }
        }
    }, [dispatch, isAuthenticated, isTeacher]);

    // When teacher classes are loaded, select the first class and fetch its files
    useEffect(() => {
        if (isTeacher && teacherClasses.length > 0 && !selectedClassId) {
            const firstClass = teacherClasses[0];
            setSelectedClassId(firstClass.id);
            setActiveTab(firstClass.name); // Set the first class tab as active
            dispatch(fetchTeacherClassFiles({ classId: firstClass.id, page: 1 }));
        }
    }, [isTeacher, teacherClasses, selectedClassId, dispatch]);

    const subjects = [
        { name: "الكل", id: 0 },
        ...(studentSubjects.map(subject => ({
            name: subject.title,
            id: subject.id
        })))
    ];

    // For teachers, create tabs for their classes
    const teacherTabs = teacherClasses.map(classItem => ({
        name: classItem.name,
        id: classItem.id
    }));

    // Handle tab change
    const handleTabChange = (tabName) => {
        setActiveTab(tabName);
        
        if (isTeacher) {
            // For teachers, handle class tabs
            const selectedClass = teacherTabs.find(tab => tab.name === tabName);
            if (selectedClass) {
                setSelectedClassId(selectedClass.id);
                dispatch(fetchTeacherClassFiles({ classId: selectedClass.id, page: 1 }));
            }
        } else {
            // For students, handle subject tabs
            const selectedSubject = subjects.find(subject => subject.name === tabName);
            if (selectedSubject) {
                dispatch(setCurrentSubject(selectedSubject.id));
                dispatch(setCurrentPage(1));
                if (isAuthenticated) {
                    dispatch(fetchFilesBySubject({ subjectId: selectedSubject.id }));
                }
            }
        }
    };

    // Handle page change
    const handlePageChange = (page) => {
        if (isTeacher) {
            dispatch(setTeacherFilesPage(page));
            if (isAuthenticated && selectedClassId) {
                dispatch(fetchTeacherClassFiles({ classId: selectedClassId, page }));
            }
        } else {
            dispatch(setCurrentPage(page));
            if (isAuthenticated) {
                dispatch(fetchFilesBySubject({ subjectId: currentSubject, page }));
            }
        }
    };

    // Handle file deletion for teachers
    const handleDeleteFile = async (fileId) => {
        if (window.confirm("هل أنت متأكد من حذف هذا الملف؟")) {
            try {
                await dispatch(deleteTeacherFile(fileId)).unwrap();
                toast.success("تم حذف الملف بنجاح");
                // Refresh the files list
                if (selectedClassId) {
                    dispatch(fetchTeacherClassFiles({ classId: selectedClassId, page: teacherFilesCurrentPage }));
                }
            } catch (error) {
                toast.error(error || "حدث خطأ في حذف الملف");
            }
        }
    };

    // Handle opening file in PDFViewer
    const handleOpenFile = (file) => {
        const fileUrl = file.file || file.url;
        const fileName = file.name || file.title;
        
        if (fileUrl) {
            // Navigate to PDFViewer with file parameters and source page
            navigate(`/pdf-viewer?url=${encodeURIComponent(fileUrl)}&name=${encodeURIComponent(fileName)}&from=files`);
        }
    };

    // Static files data for guest mode
    const staticFiles = [
        { name: "ملخص الحصة الدراسية", date: "2024-01-15", size: "2.5 MB", subject: "الرياضيات" },
        { name: "تمارين تطبيقية", date: "2024-01-14", size: "1.8 MB", subject: "العلوم" },
        { name: "شرح مفصل للمفاهيم", date: "2024-01-13", size: "3.2 MB", subject: "الدراسات الاجتماعية" },
        { name: "اختبار تجريبي", date: "2024-01-12", size: "1.5 MB", subject: "اللغة الإنجليزية" },
        { name: "ملاحظات مهمة", date: "2024-01-11", size: "0.8 MB", subject: "الرياضيات" },
        { name: "شرح الفصل الأول", date: "2024-01-10", size: "4.1 MB", subject: "العلوم" },
        { name: "تمارين إضافية", date: "2024-01-09", size: "2.2 MB", subject: "جغرافيا" },
        { name: "ملخص الوحدة", date: "2024-01-08", size: "1.9 MB", subject: "الدراسات الاجتماعية" }
    ];

    // Use API data if authenticated, otherwise use static data
    const displayFiles = isAuthenticated ? (isTeacher ? teacherFiles : files) : staticFiles;
    const currentPagination = isTeacher ? teacherFilesPagination : pagination;
    const currentLoading = isTeacher ? teacherFilesLoading : loading;
    const currentError = isTeacher ? teacherFilesError : error;

    return (
        <div className={`all-lessons-page subjects-section ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            <div className="container">
                {/* Breadcrumb */}
                <div className="lessons-breadcrumb">
                    <h1 className="breadcrumb-title">ملفاتي</h1>
                    <div className="breadcrumb-links2">
                        <span className="breadcrumb-link">الرئيسية</span>
                        <span className="breadcrumb-separator">/</span>
                        <span className="breadcrumb-current">ملفاتي</span>
                    </div>
                </div>

                {/* Tabs */}
                <div className="lessons-tabs">
                    <div className="tabs-header">
                        <div className="tabs-container">
                            {(isTeacher ? teacherTabs : subjects).map((tab) => (
                                <button
                                    key={tab.id || tab.id}
                                    className={`tab-button ${activeTab === tab.name ? 'active' : ''}`}
                                    onClick={() => handleTabChange(tab.name)}
                                >
                                    {tab.name}
                                </button>
                            ))}
                        </div>
                        {/* Add File Button for Teachers */}
                        {isTeacher && selectedClassId && (
                            <button
                                className="add-file-btn"
                                onClick={() => setIsAddFileModalOpen(true)}
                            >
                                <FaPlus />
                                إضافة ملف
                            </button>
                        )}
                    </div>
                </div>

                {/* Loading State */}
                {currentLoading && (
                    <div className="text-center my-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">جاري التحميل...</span>
                        </div>
                    </div>
                )}

                {/* Error State */}
                {currentError && (
                    <div className="text-center my-4">
                        <div className="alert alert-danger" role="alert">
                            حدث خطأ في تحميل الملفات
                        </div>
                    </div>
                )}

                {/* Files Display */}
                {!currentLoading && !currentError && (
                    <div className="lesson-files">
                        {displayFiles.length > 0 ? (
                            displayFiles.map((file, index) => (
                                <div 
                                    className="file-item clickable" 
                                    key={file.id || index}
                                    onClick={() => handleOpenFile(file)}
                                    title="انقر لفتح الملف"
                                >
                                    <div className="file-icon">
                                        <img src={'/Filetype.png'} style={{ width: '40px', height: '40px' }} alt="file-icon" />
                                    </div>
                                    <div className="file-info">
                                        <div className="file-name">
                                            {file.name || file.title}
                                        </div>
                                        <div className="file-date">{file.date || file.created_at}</div>
                                        {/* اسم المادة */}
                                        {file.subject_name && (
                                            <div className="file-subject">المادة: {file.subject_name}</div>
                                        )}
                                    </div>
                                    {/* اسم المدرس */}
                                    {file.user_name && (
                                        <div className="file-teacher">بواسطة: {file.user_name}</div>
                                    )}
                                    
                                    
                                </div>
                            ))
                        ) : (
                            <div className="text-center my-4">
                                <p>لا توجد ملفات متاحة</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Pagination */}
                {isAuthenticated && currentPagination && (
                    <div className="pagination-container mt-4">
                        <div className="pagination">
                            {Array.from({ length: currentPagination.pages_count }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    className={`pagination-btn ${(isTeacher ? teacherFilesCurrentPage : currentPage) === page ? 'active' : ''}`}
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Add Teacher File Modal */}
                <AddTeacherFileModal
                    isOpen={isAddFileModalOpen}
                    onClose={() => setIsAddFileModalOpen(false)}
                    classId={selectedClassId}
                    onFileAdded={() => {
                        if (selectedClassId) {
                            dispatch(fetchTeacherClassFiles({ classId: selectedClassId, page: 1 }));
                        }
                    }}
                />
            </div>
        </div>
    );
}

export default AllFiles; 